'use client'

import { useEffect, useState, useRef, useCallback } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { absolue, couvertureArticle } from '@/lib/blog/couverture-article'
import RichTextRenderer, { extractToc, type TocItem } from '@/components/RichTextRenderer'
import { useI18n } from '@/lib/i18n/context'
import { JsonLd, articleJsonLd, breadcrumbJsonLd } from '@/lib/jsonld'
import type { Article, ArticleCategory } from '@/lib/payload-types'
import Breadcrumbs from '@/components/Breadcrumbs'
import type { ImagesArticle } from './images-article'
import { estTraduit } from '@/lib/blog/traduction'

interface ArticleDetailProps {
  article: Article
  locale: 'en' | 'fr'
  relatedArticles?: Article[]
  /**
   * Sources des images, préparées côté serveur (./images-article.ts) : image
   * de la médiathèque lue directement dans le magasin public. À défaut, les
   * adresses de l'article (sur le domaine du site).
   */
  images?: ImagesArticle
}

// ─── Table of Contents with scroll spy ───
function TableOfContents({ items, activeId }: { items: TocItem[]; activeId: string }) {
  if (items.length === 0) return null

  return (
    <nav>
      <div className="flex items-center gap-2 mb-4">
        <div className="w-5 h-5 rounded bg-forest/10 flex items-center justify-center">
          <svg className="w-3 h-3 text-forest" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h12" />
          </svg>
        </div>
        <span className="text-xs font-bold text-gray-900 uppercase tracking-wider">Sommaire</span>
      </div>
      <div className="space-y-0.5">
        {items.map((item) => {
          const isActive = activeId === item.id
          const indent = item.level === 3 ? 'pl-4' : ''
          return (
            <a
              key={item.id}
              href={`#${item.id}`}
              onClick={(e) => {
                e.preventDefault()
                document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth' })
              }}
              className={`block py-2 text-[13px] leading-snug transition-all duration-200 border-l-[3px] pl-3 ${indent} ${
                isActive
                  ? 'border-forest text-forest font-semibold bg-forest/[0.03]'
                  : 'border-gray-200 text-gray-500 hover:text-gray-700 hover:border-gray-400'
              }`}
            >
              {item.text}
            </a>
          )
        })}
      </div>
    </nav>
  )
}

// ─── Article Card for related articles ───
function ArticleCard({ article, locale, source }: { article: Article; locale: string; source?: string | null }) {
  const { t } = useI18n()
  const b = t.blog
  const p = (path: string) => (locale === 'en' ? `/en${path}` : path)
  const firstCat = Array.isArray(article.category) ? article.category[0] : article.category
  const categoryLabel = b.categories[firstCat as ArticleCategory] || firstCat
  const vignette = source !== undefined ? source : (couvertureArticle(article)?.vignette ?? null)

  return (
    <Link href={p(`/blog/${article.slug}`)} className="group block h-full">
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300 h-full flex flex-col">
        <div className="h-48 bg-forest/5 overflow-hidden relative">
          {vignette ? (
            <Image
              src={vignette}
              alt={article.title}
              width={400}
              height={240}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-forest to-[#2E6B54]" />
          )}
          <div className="absolute top-3 left-3">
            <span className="text-[11px] font-semibold text-white bg-forest/80 backdrop-blur-sm px-2.5 py-1 rounded-full">
              {categoryLabel}
            </span>
          </div>
        </div>
        <div className="p-5 flex-1 flex flex-col">
          <h3 className="font-bold text-gray-900 group-hover:text-forest transition-colors line-clamp-2 text-[15px] leading-snug mb-2">
            {article.title}
          </h3>
          {article.excerpt && (
            <p className="text-[13px] text-gray-500 line-clamp-2 flex-1">{article.excerpt}</p>
          )}
          <div className="flex items-center gap-3 mt-3 pt-3 border-t border-gray-50">
            {article.publishedDate && (
              <span className="text-[11px] text-gray-500">
                {new Date(article.publishedDate).toLocaleDateString(locale === 'en' ? 'en-US' : 'fr-FR', {
                  month: 'short',
                  year: 'numeric',
                })}
              </span>
            )}
            {article.readTime && (
              <span className="text-[11px] text-gray-500">{article.readTime} min</span>
            )}
          </div>
        </div>
      </div>
    </Link>
  )
}

// ─── Progress bar ───
function ReadingProgress() {
  const [progress, setProgress] = useState(0)

  useEffect(() => {
    function handleScroll() {
      const el = document.getElementById('article-content')
      if (!el) return
      const rect = el.getBoundingClientRect()
      const total = el.scrollHeight - window.innerHeight
      const scrolled = -rect.top
      setProgress(Math.min(100, Math.max(0, (scrolled / total) * 100)))
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  if (progress <= 0) return null

  return (
    <div className="fixed top-0 left-0 right-0 z-50 h-[3px]">
      <div
        className="h-full bg-gradient-to-r from-forest to-forest-light transition-[width] duration-150 ease-out"
        style={{ width: `${progress}%` }}
      />
    </div>
  )
}

// ─── Main component ───
export default function ArticleDetail({ article, locale, relatedArticles = [], images }: ArticleDetailProps) {
  const { t } = useI18n()
  const b = t.blog
  const isFr = locale === 'fr'
  const isEn = locale === 'en'
  const p = (path: string) => (isEn ? `/en${path}` : path)
  const dateLocale = isEn ? 'en-US' : 'fr-FR'
  const firstCat = Array.isArray(article.category) ? article.category[0] : article.category
  const categoryLabel = b.categories[firstCat as ArticleCategory]
  // Image de l'article : même règle partout (src/lib/blog/couverture-article.ts)
  const couverture = couvertureArticle(article)

  // Sélection FR / EN : la version anglaise n'est affichée que si l'article est
  // réellement traduit (titre ET texte, src/lib/blog/traduction.ts). Sinon,
  // tout reste en français : pas de titre anglais sur un texte français.
  const a = article as any
  const enAnglais = isEn && estTraduit(a)
  const displayTitle = enAnglais ? a.titleEn : article.title
  const displayExcerpt = enAnglais && a.excerptEn ? a.excerptEn : (article.excerpt || '')
  const displayContent = enAnglais ? a.contentEn : article.content

  // Extract TOC but skip the first heading (usually intro/subtitle)
  const allTocItems = extractToc(displayContent as any)
  const tocItems = allTocItems.length > 1 ? allTocItems.slice(1) : allTocItems
  const [activeId, setActiveId] = useState('')
  const observerRef = useRef<IntersectionObserver | null>(null)

  // Scroll spy
  const setupObserver = useCallback(() => {
    if (observerRef.current) observerRef.current.disconnect()
    if (tocItems.length === 0) return

    const headingElements = tocItems
      .map((item) => document.getElementById(item.id))
      .filter(Boolean) as HTMLElement[]

    if (headingElements.length === 0) return

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const visibleEntries = entries.filter((e) => e.isIntersecting)
        if (visibleEntries.length > 0) {
          setActiveId(visibleEntries[0]?.target?.id ?? '')
        } else {
          const scrollY = window.scrollY + 150
          let lastAbove = ''
          for (const el of headingElements) {
            if (el.offsetTop <= scrollY) lastAbove = el.id
          }
          if (lastAbove) setActiveId(lastAbove)
        }
      },
      { rootMargin: '-100px 0px -65% 0px', threshold: 0 }
    )

    headingElements.forEach((el) => observerRef.current!.observe(el))
  }, [tocItems])

  useEffect(() => {
    const timer = setTimeout(setupObserver, 300)
    return () => {
      clearTimeout(timer)
      observerRef.current?.disconnect()
    }
  }, [setupObserver])

  // Prepare JSON-LD schema data
  const featuredImageUrl = couverture ? absolue(couverture.url) : undefined

  // Extract author info if it's a populated user object
  let authorInfo: { name?: string; email?: string } | null = null
  if (article.author && typeof article.author === 'object' && 'name' in article.author) {
    authorInfo = {
      name: (article.author as any).name,
      email: (article.author as any).email,
    }
  }

  const articleSchema = articleJsonLd({
    title: displayTitle,
    slug: article.slug,
    excerpt: displayExcerpt,
    publishedDate: article.publishedDate ? new Date(article.publishedDate).toISOString() : new Date().toISOString(),
    updatedDate: article.updatedAt ? new Date(article.updatedAt).toISOString() : undefined,
    category: firstCat as string,
    imageUrl: featuredImageUrl,
    author: authorInfo,
  })

  const breadcrumbItems = [
    { name: locale === 'fr' ? 'Accueil' : 'Home', url: '/' },
    { name: locale === 'fr' ? 'Blog' : 'Blog', url: '/blog' },
    { name: displayTitle, url: `/blog/${article.slug}` },
  ]
  const breadcrumbSchema = breadcrumbJsonLd(breadcrumbItems)

  return (
    <>
      <JsonLd data={articleSchema} />
      <JsonLd data={breadcrumbSchema} />
      <ReadingProgress />

      {/* ═══ Hero – full width, immersive ═══ */}
      <section className="gradient-forest pt-28 pb-40 relative overflow-hidden">
        {/* Decorative blurs */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 left-[10%] w-72 h-72 bg-white/5 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-[15%] w-96 h-96 bg-white/[0.03] rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 max-w-[900px] mx-auto px-6 text-center">
          {/* Breadcrumb */}
          <div className="flex justify-center mb-5">
            <Breadcrumbs
              variant="light"
              items={[
                { label: isFr ? 'Accueil' : 'Home', href: isFr ? '/' : '/en' },
                { label: 'Blog', href: isFr ? '/blog' : '/en/blog' },
                { label: displayTitle },
              ]}
            />
          </div>
          {/* Category + meta */}
          <div className="flex items-center justify-center gap-3 mb-6 flex-wrap">
            <span className="bg-amber/90 text-forest text-xs font-bold px-3.5 py-1.5 rounded-full uppercase tracking-wide">
              {categoryLabel}
            </span>
            <div className="flex items-center gap-2 text-white/50 text-xs">
              {article.readTime && (
                <>
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{article.readTime} min de lecture</span>
                </>
              )}
              {article.publishedDate && (
                <>
                  <span className="text-white/30">·</span>
                  <span>
                    {new Date(article.publishedDate).toLocaleDateString(dateLocale, {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Title */}
          <h1 className="font-heading font-extrabold text-[2.5rem] md:text-5xl lg:text-[3.25rem] text-white leading-[1.15] tracking-tight">
            {displayTitle}
          </h1>

          {/* Excerpt */}
          {displayExcerpt && (
            <p className="text-white/60 mt-5 text-lg leading-relaxed max-w-[700px] mx-auto">
              {displayExcerpt}
            </p>
          )}
        </div>
      </section>

      {/* Featured image – full width, pulled up into the hero */}
      <div className="bg-cream">
        <div className="max-w-[1200px] mx-auto px-6 -mt-28 relative z-10">
          <div className="rounded-2xl overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.12)] aspect-[2/1] bg-forest/10">
            {couverture ? (
              <Image
                src={images?.une ?? couverture.url}
                alt={displayTitle}
                width={1000}
                height={562}
                className="w-full h-full object-cover"
                priority
                fetchPriority="high"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-forest to-[#2E6B54]" />
            )}
          </div>
        </div>
      </div>

      {/* ═══ Breadcrumb bar ═══ */}
      <div className="bg-cream border-b border-gray-100">
        <div className="max-w-[1200px] mx-auto px-6 py-3">
          <nav className="flex items-center gap-2 text-xs text-gray-500">
            <Link href={p('/')} className="hover:text-forest transition-colors">Accueil</Link>
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <Link href={p('/blog')} className="hover:text-forest transition-colors">Blog</Link>
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="text-gray-600 font-medium truncate max-w-[300px]">{displayTitle}</span>
          </nav>
        </div>
      </div>

      {/* ═══ Article body with TOC sidebar ═══ */}
      <section className="bg-cream py-10 lg:py-14" id="article-content">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="flex gap-12">

            {/* Left: Sticky TOC */}
            {tocItems.length > 1 && (
              <aside className="hidden lg:block w-56 flex-shrink-0">
                <div className="sticky top-28">
                  <TableOfContents items={tocItems} activeId={activeId} />

                  {/* Share / back */}
                  <div className="mt-8 pt-6 border-t border-gray-200">
                    <Link
                      href={p('/blog')}
                      className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-forest transition-colors"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                      Retour au blog
                    </Link>
                  </div>
                </div>
              </aside>
            )}

            {/* Center: Article content */}
            <article className="flex-1 min-w-0 max-w-[760px]">
              <div className="bg-white rounded-2xl p-8 md:p-10 lg:p-14 border border-gray-100/80 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
                {displayContent ? (
                  <RichTextRenderer data={displayContent as any} />
                ) : (
                  <p className="text-gray-600 text-lg leading-relaxed">{displayExcerpt}</p>
                )}
              </div>

              {/* Tags */}
              {article.tags && article.tags.length > 0 && (
                <div className="mt-6 flex items-center gap-2 flex-wrap">
                  <svg className="w-4 h-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                  {article.tags.map((tg, i) => (
                    <span key={i} className="text-xs bg-gray-100 text-gray-500 px-3 py-1 rounded-full hover:bg-forest/10 hover:text-forest transition-colors cursor-default">
                      {tg.tag}
                    </span>
                  ))}
                </div>
              )}

              {/* Article CTA */}
              {article.cta?.enabled && article.cta.title && (
                <div className="mt-10 bg-gradient-to-br from-forest to-forest-light rounded-2xl p-8 md:p-10 text-center text-white">
                  <h3 className="text-xl font-bold mb-2">{article.cta.title}</h3>
                  {article.cta.description && (
                    <p className="text-white/70 mb-5 max-w-md mx-auto">{article.cta.description}</p>
                  )}
                  {article.cta.buttonText && article.cta.buttonUrl && (
                    <a
                      href={article.cta.buttonUrl}
                      className="inline-block bg-amber text-forest font-bold px-7 py-3 rounded-full hover:brightness-110 transition-all"
                    >
                      {article.cta.buttonText}
                    </a>
                  )}
                </div>
              )}
            </article>
          </div>
        </div>
      </section>

      {/* ═══ Related articles ═══ */}
      {relatedArticles.length > 0 && (
        <section className="py-16 lg:py-20 bg-white">
          <div className="max-w-[1200px] mx-auto px-6">
            <div className="flex items-end justify-between mb-10">
              <div>
                <span className="text-xs font-bold text-forest uppercase tracking-wider">
                  {locale === 'fr' ? 'Pour aller plus loin' : 'Keep reading'}
                </span>
                <h2 className="font-heading font-bold text-2xl md:text-3xl text-gray-900 mt-1">
                  {locale === 'fr' ? 'Articles similaires' : 'Related articles'}
                </h2>
              </div>
              <Link
                href={p('/blog')}
                className="hidden md:inline-flex items-center gap-1.5 text-sm text-forest font-medium hover:text-forest-light transition-colors"
              >
                {b.allArticles}
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedArticles.slice(0, 3).map((related) => (
                <ArticleCard key={related.id} article={related} locale={locale} source={images?.vignettesLiees[related.slug]} />
              ))}
            </div>
            <div className="text-center mt-8 md:hidden">
              <Link
                href={p('/blog')}
                className="text-forest font-medium text-sm inline-flex items-center gap-1"
              >
                {b.allArticles}
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
          </div>
        </section>
      )}
    </>
  )
}
